import { useEffect, useState } from "react";

import { type TCompanyResponse } from "@/api/companies";
import { useStore } from "@/store";

type TUseOnboardingGuardResult = {
  isComplete: boolean;
  isLoading: boolean;
  company: TCompanyResponse | null;
  error: string | null;
};

/**
 * Hook to check if a company's onboarding is complete.
 * Uses store to get company data and checks the onboarding_completed status.
 *
 * @param companyId - The company ID to check
 * @returns Object with isComplete, isLoading, company, and error
 */
export function useOnboardingGuard(companyId: string | undefined): TUseOnboardingGuardResult {
  const companies = useStore((state) => state.companies);
  const isLoading = useStore((state) => state.isLoading);
  const error = useStore((state) => state.error);
  const fetchCompanies = useStore((state) => state.fetchCompanies);
  const getCompanyById = useStore((state) => state.getCompanyById);
  const [isComplete, setIsComplete] = useState(false);
  const [company, setCompany] = useState<TCompanyResponse | null>(null);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    // Fetch companies if not already loaded
    void fetchCompanies({ force: false });
  }, [companyId, fetchCompanies]);

  useEffect(() => {
    if (!companyId) {
      setCompany(null);
      setIsComplete(false);
      return;
    }

    const foundCompany = getCompanyById(companyId);
    setCompany(foundCompany);
    setIsComplete(foundCompany?.onboarding_completed ?? false);
  }, [companyId, companies, getCompanyById]);

  return { isComplete, isLoading, company, error };
}
